(ns serv-ui.login
  "Signing in: an address, a code that goes to it, and the code typed back
  with the device the key is for - the server wants one. The browser's
  user agent is offered until a device has been given here. The key the
  server answers with is kept (`serv-ui.auth`), and so is the device, for
  next time.

  Opened on a prompt's link (`prompt-id`), the address is the prompt's: the
  code goes to its recipient at once and only the code is asked for."
  (:require-macros [kc.core :refer [def-ui $]])
  (:require [clojure.string :as str]
            [kc.core :as kc]
            [serv-ui.api :as api]
            [serv-ui.auth :as auth]
            [serv-ui.mui :as mui :refer [sx]]))

(defn- only-digits [s]
  (let [d (str/replace (or s "") #"[^0-9]" "")]
    (subs d 0 (min 6 (count d)))))

(defn- address? [s]
  (boolean (re-matches #"[^@\s]+@[^@\s.]+\.[^@\s]+" (str/trim (or s "")))))

(def-ui Login [on-login prompt-id title]
  (let [[email set-email] (kc/use-state (or (auth/last-email) ""))
        [sent? set-sent]  (kc/use-state false)
        [code set-code]   (kc/use-state "")
        [device set-device] (kc/use-state (or (auth/last-device) (.-userAgent js/navigator) ""))
        ready?  (and (= 6 (count code)) (not (str/blank? device)))
        [busy? set-busy]  (kc/use-state false)
        [error set-error] (kc/use-state nil)
        [for-prompt? set-for-prompt] (kc/use-state (boolean prompt-id))
        sent!   (fn []
                  (set-sent true)
                  (set-busy false)
                  (some-> (.getElementById js/document "serv-code") .focus))
        prompt-code! (fn []
                       (set-busy true)
                       (set-error nil)
                       (-> (api/request> :post (str "/api/operator-prompts/"
                                                    (js/encodeURIComponent prompt-id) "/code"))
                           (.then (fn [{e :email}]
                                    (set-email e)
                                    (auth/remember-email! e)
                                    (sent!)))
                           (.catch (fn [e]
                                     (set-busy false)
                                     (set-for-prompt false)
                                     (set-error (if (= 404 (:status (ex-data e)))
                                                  "No such prompt. Sign in with your address."
                                                  (str "The code could not be requested: " (ex-message e))))))))
        send!   (fn []
                  (if for-prompt?
                    (prompt-code!)
                  (when (address? email)
                    (set-busy true)
                    (set-error nil)
                    (auth/remember-email! (str/trim email))
                    (-> (api/request> :post "/api/auth/code" {:email (str/trim email)})
                        (.then (fn [_]
                                 (set-sent true)
                                 (set-busy false)
                                 (some-> (.getElementById js/document "serv-code") .focus)))
                        (.catch (fn [e]
                                  (set-busy false)
                                  (set-error (str "The code could not be requested: " (ex-message e)))))))))
        submit! (fn []
                  (set-busy true)
                  (set-error nil)
                  (auth/remember-device! (str/trim device))
                  (-> (api/request> :post "/api/auth/login"
                                    {:email (str/trim email) :code code :device (str/trim device)})
                      (.then (fn [{:keys [key]}]
                               (auth/remember-key! key)
                               (set-busy false)
                               (on-login)))
                      (.catch (fn [e]
                                (set-busy false)
                                (if (= 401 (:status (ex-data e)))
                                  (do (set-code "") (set-error "Wrong or expired code."))
                                  (set-error (ex-message e)))))))]
    (kc/use-effect
     [prompt-id]
     (when prompt-id (prompt-code!))
     js/undefined)
    ($ mui/Box {:sx (sx {:minHeight "100vh" :display "flex"
                         :alignItems "center" :justifyContent "center" :padding "1rem"})}
       ($ mui/Paper {:variant "outlined"
                     :sx (sx {:display "flex" :flexDirection "column" :gap "1rem"
                              :padding "1.5rem" :width "100%" :maxWidth "22rem"})}
          ($ mui/Typography {:variant "h6"} (or title "Kunagi Serv"))
          ($ mui/Typography {:sx (sx {:color "text.secondary"})}
             (cond sent?       (str "A code is on its way to " (str/trim email) ".")
                   for-prompt? "A code goes to whom this prompt is for..."
                   :else       "Type your e-mail address and a code goes to it."))
          ($ mui/TextField
             {:value email
              :label "E-Mail"
              :type "email"
              :size "small"
              :autoFocus (not sent?)
              :disabled sent?
              :autoComplete "email"
              :onChange (fn [^js e] (set-email (-> e .-target .-value)))
              :onKeyDown (fn [^js e]
                           (when (and (= "Enter" (.-key e)) (address? email))
                             (.preventDefault e)
                             (send!)))})
          (when sent?
            ($ mui/TextField
               {:id "serv-code"
                :value code
                :placeholder "------"
                :autoFocus true
                :size "small"
                :autoComplete "one-time-code"
                :slotProps (sx {:htmlInput {:aria-label "Code"
                                            :inputMode "numeric"
                                            :pattern "[0-9]*"
                                            :maxLength 6
                                            :style {:letterSpacing ".5em"
                                                    :textAlign "center"
                                                    :fontSize "1.4rem"}}})
                :onChange (fn [^js e] (set-code (only-digits (-> e .-target .-value))))
                :onKeyDown (fn [^js e]
                             (when (and (= "Enter" (.-key e)) ready?)
                               (.preventDefault e)
                               (submit!)))}))
          (when sent?
            ($ mui/TextField
               {:value device
                :label "Device"
                :helperText "Which device or app this key is for"
                :size "small"
                :required true
                :onChange (fn [^js e] (set-device (-> e .-target .-value)))
                :onKeyDown (fn [^js e]
                             (when (and (= "Enter" (.-key e)) ready?)
                               (.preventDefault e)
                               (submit!)))}))
          (when error
            ($ mui/Alert {:severity "error" :variant "outlined"} error))
          ($ mui/Box {:sx (sx {:display "flex" :alignItems "center" :gap ".5rem"})}
             ($ mui/Button {:disabled (or busy? (not (address? email)))
                            :onClick (fn [_] (send!))
                            :variant (if sent? "outlined" "contained")
                            :disableElevation true
                            :color (if sent? "inherit" "primary")}
                (if sent? "New code" "Request code"))
             ($ :span {:style {:flex "1"}})
             (when sent?
               ($ mui/Button {:disabled (or busy? (not ready?))
                              :variant "contained"
                              :disableElevation true
                              :onClick (fn [_] (submit!))}
                  "Sign in")))
          ($ mui/Typography {:variant "caption" :sx (sx {:color "text.disabled" :textAlign "right"})}
             (api/build))))))
